/**
 * Quiz — "Find Your Ritual" / RESULTS page
 * -----------------------------------------------------------------------------
 * STANDALONE script. This file is NOT a webpack build output — it is
 * hand-authored and safe to edit directly, following the same pattern as
 * assets/quiz-ritual.js and assets/croev-quiz-v1.js. Deliberately kept out of
 * dev/src/js/sections/ (which webpack auto-globs) so that adding the results
 * page never forces a rebuild of vendor.js / common.js / theme.js.
 * See CLAUDE.md "Webpack JS bundle drift".
 *
 * Paired with: sections/quiz-results-header.liquid,
 *              snippets/quiz-results-concerns.liquid,
 *              assets/quiz-results.css
 *
 * WHY sessionStorage: the results page is a SEPARATE page load from the quiz,
 * so nothing can be handed over in memory. assets/quiz-ritual.js persists every
 * answer under `froya:quiz:ritual:v1`; this reads that key back. A visitor
 * without a `completedAt` in that record never reaches this script — the
 * inline gate in quiz-results-header.liquid redirects them to the quiz first.
 * The per-slot fallbacks and the Mature default remain as belt-and-braces for
 * a finished quiz with a missing answer.
 *
 * WHAT IT DOES: nothing is built at runtime. Every copy variant is already in
 * the DOM, server-rendered and hidden by Liquid; this only reveals the matching
 * one. That keeps the copy in Liquid (editable, translatable, no duplication
 * into JS) and means there is never a flash of the wrong variant.
 *
 * Public surface:
 *   window.FroyaRitualResults.getAnswers()  -> { age: 'under_30', ... }
 *   window.FroyaRitualResults.getIdentity() -> { first_name, ... }
 *   window.FroyaRitualResults.getBundle()   -> 'mature_skin' | 'acne' | 'natural_pharmacy'
 *   document 'quiz-results:ready'           -> { answers, identity, bundle }
 */
(function () {
  'use strict';

  var STORAGE_VERSION = 1;
  var DEFAULT_KEY = 'froya:quiz:ritual:v1';

  /**
   * Q2 -> recommended bundle. Straight from the brief:
   *   wrinkles / dark circles / dull tone -> Mature Women's Skin
   *   acne / rosacea                      -> Acne System
   *   eczema, psoriasis, dry              -> Natural Pharmacy
   * Every Q2 answer maps, so the fallback below only fires when Q2 itself is
   * missing — which is reachable, since the quiz persists on every step and a
   * visitor can arrive here having never answered it.
   */
  var BUNDLE_BY_CONCERN = {
    wrinkles_aging: 'mature_skin',
    dark_circles: 'mature_skin',
    dull_tone: 'mature_skin',
    acne_breakouts: 'acne',
    rosacea_redness: 'acne',
    eczema_dry: 'natural_pharmacy'
  };

  var DEFAULT_BUNDLE = 'mature_skin';

  /* ---------------------------------------------------------------- state -- */

  function readState(key) {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(key);
    } catch (e) {
      /* private mode / storage disabled — fall through to the defaults */
    }
    if (!raw) return null;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    // Version guard mirrors the quiz's own: a shape written by an older build
    // is ignored rather than half-read into the wrong slots.
    if (!saved || saved.v !== STORAGE_VERSION) return null;
    return saved;
  }

  /* ----------------------------------------------------------------- dom -- */

  function reveal(root, attr, value) {
    var nodes = root.querySelectorAll('[' + attr + ']');
    var matched = false;

    for (var i = 0; i < nodes.length; i++) {
      var own = nodes[i].getAttribute(attr);
      var on = own === value;
      nodes[i].hidden = !on;
      if (on) matched = true;
    }

    // Nothing matched (answer missing, or a slug the markup does not carry) —
    // fall back so the sentence still reads instead of collapsing to nothing.
    if (!matched) {
      for (var j = 0; j < nodes.length; j++) {
        nodes[j].hidden = nodes[j].getAttribute(attr) !== 'fallback';
      }
    }
    return matched;
  }

  /**
   * Concern bullets: the Q2 pick leads, then the Q3 picks.
   *
   * ORDER: the Q3 bullets render "in the order picked" (copy-logic doc §3).
   * quiz-ritual.js commits a multi-select in tap order — it keeps the saved
   * array's order for anything still selected and appends new taps — so the
   * array read from sessionStorage IS the pick order and is used as-is.
   *
   * DEDUPE: `dull_tone` and `dark_circles` are offered in BOTH questions, so
   * the same concern can be picked twice and would otherwise render two
   * near-identical bullets ("Dull, Uneven Skin Tone" / "Dull, Uneven Tone").
   * The Q3 repeat is dropped. Neither the brief nor Figma covers this.
   *
   * Ordering is done with the CSS `order` property rather than by moving
   * nodes — same technique as the quiz's Q1 reviews slider.
   */
  function revealBullets(root, answers) {
    var list = root.querySelector('[data-quiz-results-list]');
    if (!list) return;

    var picked = [];
    var seen = {};

    if (answers.main_concern) {
      picked.push('main_concern:' + answers.main_concern);
      seen[answers.main_concern] = true;
    }

    var secondary = answers.secondary_concerns;
    if (typeof secondary === 'string') secondary = [secondary];
    if (Object.prototype.toString.call(secondary) === '[object Array]') {
      for (var i = 0; i < secondary.length; i++) {
        var slug = secondary[i];
        // "none of these" must never produce a bullet.
        if (!slug || slug === 'none' || seen[slug]) continue;
        seen[slug] = true;
        picked.push('secondary_concerns:' + slug);
      }
    }

    var items = list.querySelectorAll('[data-quiz-results-bullet]');
    for (var j = 0; j < items.length; j++) {
      var key = items[j].getAttribute('data-quiz-results-bullet');
      var rank = indexOf(picked, key);
      items[j].hidden = rank === -1;
      if (rank !== -1) items[j].style.order = String(rank);
    }
  }

  function indexOf(arr, value) {
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] === value) return i;
    }
    return -1;
  }

  /**
   * The template's section keys, per bundle: one buy box and one stats section
   * each. All three of each are rendered and the matching pair is revealed —
   * the pattern page.quiz.json already uses in production, so market-correct
   * pricing is server-rendered for free and only visibility changes here.
   *
   * SAFE BECAUSE the bundles are single-variant with no quantity selector.
   * The buy box carries hardcoded ids (#purchase-type-fieldset, #custom-product-form)
   * and _component-product-form-new.js has ~19 document-level querySelectors,
   * so three copies do collide — but only inside its variant/quantity/currency
   * UPDATE paths, which these products never trigger. The purchase-type toggle
   * is scoped via closest('c-subscription'), so switching is safe.
   * If a bundle ever gains real variants or show_quantity_selectors is turned
   * on here, updatePriceBlock() starts writing to the FIRST .js-price-block on
   * the page — i.e. the wrong bundle's price. Don't do that without moving to
   * one-buy-box-per-template (?view=).
   */

  var SECTION_KEYS_BY_BUNDLE = {
    mature_skin: ['bundle_mature', 'stats_mature'],
    acne: ['bundle_acne', 'stats_acne'],
    natural_pharmacy: ['bundle_pharmacy', 'stats_pharmacy']
  };

  var VISIBLE_CLASS = 'is-quiz-bundle-visible';

  /**
   * A JSON template renders sections as `shopify-section-template--<hash>__<key>`,
   * and a template cannot put attributes on that wrapper — so sections are
   * addressed by key suffix. Same resolver as section-quiz-funnel.js, including
   * the plain `shopify-section-<key>` fallback for a statically rendered section.
   */
  function findSectionEl(key) {
    return document.querySelector('[id^="shopify-section"][id$="__' + key + '"]')
      || document.getElementById('shopify-section-' + key);
  }

  /**
   * The buy-box anchor. Every lp-main-product renders
   * `<div class="s-main-product__container" id="{{ anchor_id }}">` (default
   * "product-section"), so with three buy boxes on the page there are three
   * identical ids and document.getElementById() — which is what
   * __froyaScrollTo (snippets/lp-smooth-scroll.liquid) uses first — returns
   * the FIRST one, i.e. bundle_mature, even when it is display:none. A hidden
   * element has a zero rect, so every "scroll to the buy box" CTA lower on the
   * page would scroll nowhere for acne / pharmacy. Fix: keep the id only on
   * the visible bundle's container; park it on the hidden ones so a re-run
   * (bundle change) can restore it.
   */
  var ANCHOR_PARK_ATTR = 'data-quiz-parked-anchor';

  function syncBuyBoxAnchor(sectionEl, visible) {
    var container = sectionEl.querySelector('.s-main-product__container');
    if (!container) return; // stats sections have no buy box
    if (visible) {
      if (!container.id && container.getAttribute(ANCHOR_PARK_ATTR)) {
        container.id = container.getAttribute(ANCHOR_PARK_ATTR);
      }
    } else if (container.id) {
      container.setAttribute(ANCHOR_PARK_ATTR, container.id);
      container.removeAttribute('id');
    }
  }

  function revealBundle(bundle) {
    var wanted = SECTION_KEYS_BY_BUNDLE[bundle] || [];

    for (var key in SECTION_KEYS_BY_BUNDLE) {
      if (!Object.prototype.hasOwnProperty.call(SECTION_KEYS_BY_BUNDLE, key)) continue;
      var keys = SECTION_KEYS_BY_BUNDLE[key];
      for (var i = 0; i < keys.length; i++) {
        var el = findSectionEl(keys[i]);
        if (!el) continue;
        var visible = indexOf(wanted, keys[i]) !== -1;
        if (visible) el.classList.add(VISIBLE_CLASS);
        else el.classList.remove(VISIBLE_CLASS);
        syncBuyBoxAnchor(el, visible);
      }
    }
  }

  /**
   * Line properties that trigger the quiz discount.
   * ---------------------------------------------------------------------------
   * The discount is applied by the EXISTING "Froya Bundle Discounts" app
   * (~/Froya-bundle-discounts), whose discount-function-js extension already
   * targets cart.lines.discounts.generate.run. No new app, and no function code
   * is needed — the offer is a row in the shop metafield $app:bundle_config,
   * matched to a cart line by these two line properties.
   *
   * BOTH ARE SET ON EVERY ADD, deliberately. The function's paths are mutually
   * exclusive: subscription_bundle applies only to lines WITH a
   * sellingPlanAllocation, and every other path does `if (line.sellingPlanAllocation) continue`.
   * So the line's own selling plan decides which row applies, and the page does
   * not have to track the purchase-type toggle at all.
   *
   * NOT A SECURITY BOUNDARY. These are line properties on a public form, so
   * anyone can post them and self-grant the discount. Functions are pure — no
   * network, no clock — so a signed token cannot be validated inside one. What
   * this buys over a discount code is that there is no memorable string to leak
   * onto a coupon site and no discount-code line at checkout. It does not stop
   * a determined person. Same class as the $0 free-gift issue.
   */
  var PROP_OTP = '_froya_bundle_id';
  var PROP_SUB = '_froya_sub_bundle_id';

  function setHiddenInput(form, name, value) {
    var existing = form.querySelector('input[name="' + name + '"]');
    if (existing) {
      existing.value = value;
      return;
    }
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  /**
   * The app's config, parsed from the <script type="application/json"> that
   * quiz-results-header.liquid emits.
   *
   * WHY IN JS: the app stores the config as a JSON *string*, and Liquid has no
   * JSON parser. Matching rows in Liquid silently yielded nothing every time
   * (a non-empty string is not `blank`, so it even reported the config as
   * present). snippets/bundle-buy-box.liquid solves it the same way — emit the
   * raw string, JSON.parse it here.
   */
  function readPromoConfig() {
    var el = document.querySelector('script[type="application/json"][data-quiz-promo-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /** "gid://shopify/ProductVariant/123" and "123" must both match "123". */
  function sameVariant(rowVariant, variantId) {
    var raw = String(rowVariant == null ? '' : rowVariant);
    if (!raw || !variantId) return false;
    if (raw === variantId) return true;
    var parts = raw.split('/');
    return parts[parts.length - 1] === variantId;
  }

  /**
   * Finds the one-time and subscription promo rows for a (template, variant).
   * All three bundles share one template, so items[].variantId is what
   * identifies which bundle a row belongs to.
   */
  function findPromoIds(config, templateKey, variantId) {
    var out = { otp: '', sub: '' };
    var rows = config && config.bundles;
    if (Object.prototype.toString.call(rows) !== '[object Array]') return out;

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row || row.active === false) continue;
      if (String(row.template || '').trim() !== templateKey) continue;

      var items = row.items;
      if (Object.prototype.toString.call(items) !== '[object Array]') continue;

      var hit = false;
      for (var j = 0; j < items.length; j++) {
        if (items[j] && sameVariant(items[j].variantId, variantId)) { hit = true; break; }
      }
      if (!hit) continue;

      if (row.promoType === 'subscription_bundle') {
        if (!out.sub) out.sub = row.id || '';
      } else if (!out.otp) {
        out.otp = row.id || '';
      }
    }
    return out;
  }

  /**
   * Tags the revealed bundle's add-to-cart form(s) so the app's discount
   * function can match the line.
   *
   * The theme submits via `new FormData(this.elements.form)`, so inputs
   * appended here are picked up with no change to component-product-form-new.liquid,
   * which is shared by 57 templates and best left alone.
   */
  function tagBundleForms(bundle) {
    var config = readPromoConfig();
    var keys = SECTION_KEYS_BY_BUNDLE[bundle] || [];

    for (var i = 0; i < keys.length; i++) {
      var section = findSectionEl(keys[i]);
      if (!section) continue;

      var tag = section.querySelector('[data-quiz-promo-tag]');
      if (!tag) continue;

      var ids = findPromoIds(config, tag.dataset.templateKey, tag.dataset.variantId);

      // Left deliberately loud: the buy box still shows the offer price from
      // its section settings, so a missing row means display and charge
      // disagree. Silent failure here is a billing bug.
      if (!ids.otp && !ids.sub) {
        if (window.console && console.warn) {
          console.warn('[quiz-results] no Bundle Discounts promo row matched; the buy box price will NOT match the cart', {
            template: tag.dataset.templateKey,
            variant: tag.dataset.variantId,
            configFound: !!config,
            rowCount: config && config.bundles ? config.bundles.length : 0
          });
        }
        continue;
      }

      var forms = section.querySelectorAll('form');
      for (var j = 0; j < forms.length; j++) {
        if (ids.otp) setHiddenInput(forms[j], 'properties[' + PROP_OTP + ']', ids.otp);
        if (ids.sub) setHiddenInput(forms[j], 'properties[' + PROP_SUB + ']', ids.sub);
      }
    }
  }

  /**
   * Tags the cart drawer's empty-state quick-add so it charges the same price
   * as the buy box.
   * ---------------------------------------------------------------------------
   * The empty mini-cart renders `featured_products` as snippets/
   * mini-cart-product-card.liquid quick-add cards, and that form carries only
   * `id` / `quantity` / `selling_plan` — no `properties[…]`. That list is
   * currently ONE product and it is a quiz bundle
   * (the-complete-system-for-mature-women-s-skin), so opening an empty drawer
   * on this page and adding from it charged full price while the buy box a few
   * hundred pixels away charged the promo price.
   *
   * Scoped deliberately to this page: the drawer is rendered site-wide by
   * `{% section 'mini-cart' %}` in theme.liquid, and the promo config JSON only
   * exists on this template, so nothing here can leak the quiz discount onto a
   * PDP or the homepage.
   *
   * Scoped a second time to the MATCHED bundle: the promo is only attached when
   * the quiz routed this visitor to the very bundle the card sells. A visitor
   * sent to Acne or Natural Pharmacy who quick-adds the mature-skin system pays
   * its normal price — the discount is the offer on the recommendation, not a
   * standing price on the product. That is why the variant is taken from the
   * revealed bundle's own promo tag and compared to the card, instead of
   * matching the card straight against the config.
   *
   * A capture-phase listener rather than tagging the card up front, for two
   * reasons: the drawer re-renders itself from `sections: ['mini-cart']` on
   * every cart refresh (which would wipe an injected input), and the card's
   * button is an inline `onclick="customAddToCartHelper(this)"` — a capture
   * listener on the document always runs before a target's own handler, so the
   * inputs are in place by the time the helper reads the form.
   */
  function tagDrawerQuickAdds(bundle) {
    var config = readPromoConfig();
    if (!config) return;

    // The revealed bundle's own promo tag — the same (template, variant) pair
    // tagBundleForms uses, so the drawer and the buy box can never disagree.
    var keys = SECTION_KEYS_BY_BUNDLE[bundle] || [];
    var tag = null;
    for (var i = 0; i < keys.length; i++) {
      var section = findSectionEl(keys[i]);
      if (!section) continue;
      tag = section.querySelector('[data-quiz-promo-tag]');
      if (tag) break;
    }
    if (!tag || !tag.dataset.templateKey || !tag.dataset.variantId) return;

    var bundleVariantId = tag.dataset.variantId;
    var ids = findPromoIds(config, tag.dataset.templateKey, bundleVariantId);
    if (!ids.otp && !ids.sub) return;

    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest('.drawer__product-form__cta')
        : null;
      if (!button) return;

      var form = button.closest('form');
      if (!form) return;

      var idInput = form.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) return;

      // The gate: this card must be selling the bundle the quiz matched.
      if (!sameVariant(bundleVariantId, String(idInput.value))) {
        // A DIFFERENT quiz bundle in the drawer (its variant still resolves to
        // promo rows) gets neither the discount nor the free gift: the gift is
        // part of the offer on the recommendation, so a visitor routed to Acne
        // must not collect the mature system's gift by quick-adding it here.
        //
        // The suppression MUST be a cart line property, and ONLY that. The
        // gift engine (gift-autoadd-extension.js) honours _froya_no_gift in two
        // places: giftAddToCart() skips getApplicableGifts() when the form
        // carries it, so no gift is added in the first place; and
        // analyzeCartItems() drops such lines from product-rule matching, so
        // the reconciler — which re-derives gifts from /cart.js on every cart
        // refresh — never adds one back. The line still counts toward the
        // subtotal, so spend-threshold rules are unaffected.
        //
        // Do NOT touch data-gift-trigger here. Blanking it sends
        // customAddToCartHelper down its fallback branch, whose
        // GiftDetectionService re-derives the gift from data-product-handle
        // and adds it anyway — that is exactly how the first attempt at this
        // made things worse.
        var other = findPromoIds(config, tag.dataset.templateKey, String(idInput.value));
        if (other.otp || other.sub) {
          setHiddenInput(form, 'properties[_froya_no_gift]', '1');
        }
        return;
      }

      // Both keys go on, exactly as tagBundleForms does: the discount function
      // picks the one-time row (_froya_bundle_id, no selling plan) or the
      // subscription row (_froya_sub_bundle_id, selling plan present) from the
      // line itself, and this card may add either depending on the product type.
      if (ids.otp) setHiddenInput(form, 'properties[' + PROP_OTP + ']', ids.otp);
      if (ids.sub) setHiddenInput(form, 'properties[' + PROP_SUB + ']', ids.sub);
    }, true);
  }

  /**
   * Final CTA section (sections/quiz-results-final-cta.liquid).
   * ---------------------------------------------------------------------------
   * Its price row and button are LIVE mirrors of the revealed buy box, never a
   * second copy of the offer: the buy box owns the price, promo %, selling
   * plan, gift and discount line properties, and a duplicate here would drift.
   *
   *  - price / compare-at / save pill are copied from the toggle's
   *    .c-buybox-toggle__price--sub|--otp and __save-pill--sub|--otp spans,
   *    picking the one that matches the current purchase type. The one-time
   *    state is the class c-subscribtion--bbt-otp that buybox-switch-toggle
   *    toggles on <c-subscription>, so a MutationObserver on that class keeps
   *    the mirror in step with the switch.
   *  - the button proxy-clicks the buy box's real add-to-cart
   *    (.js-subscribtion__add-to-cart), so the cart add is the exact request
   *    the buy box would make — same FormData, same tagged properties. If that
   *    button is missing or disabled the click smooth-scrolls to the buy box
   *    instead (lp-smooth-scroll's helper).
   */
  function bindFinalCta(bundle) {
    var ctas = document.querySelectorAll('[data-quiz-final-cta]');
    if (!ctas.length) return;

    var keys = SECTION_KEYS_BY_BUNDLE[bundle] || [];
    var buyBox = null;
    for (var i = 0; i < keys.length && !buyBox; i++) {
      var el = findSectionEl(keys[i]);
      if (el && el.querySelector('.c-subscribtion')) buyBox = el;
    }
    if (!buyBox) return;

    var sub = buyBox.querySelector('.c-subscribtion');

    function text(sel) {
      var node = buyBox.querySelector(sel);
      return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
    }

    function sync() {
      var otp = sub && sub.classList.contains('c-subscribtion--bbt-otp');
      var state = otp ? 'otp' : 'sub';
      var main = text('.c-buybox-toggle__price--' + state + ' .c-buybox-toggle__price-main');
      var compare = text('.c-buybox-toggle__price--' + state + ' .c-buybox-toggle__price-compare');
      var pill = text('.c-buybox-toggle__save-pill--' + state);
      for (var c = 0; c < ctas.length; c++) {
        var row = ctas[c].querySelector('[data-quiz-final-priceline]');
        var p = ctas[c].querySelector('[data-quiz-final-price]');
        var cmp = ctas[c].querySelector('[data-quiz-final-compare]');
        var pl = ctas[c].querySelector('[data-quiz-final-pill]');
        if (p) p.textContent = main;
        if (cmp) cmp.textContent = compare;
        if (pl) pl.textContent = pill;
        if (row) {
          if (main) row.removeAttribute('hidden');
          else row.setAttribute('hidden', '');
        }
      }
    }

    sync();
    if (sub && window.MutationObserver) {
      new MutationObserver(sync).observe(sub, { attributes: true, attributeFilter: ['class'] });
    }

    for (var b = 0; b < ctas.length; b++) {
      var button = ctas[b].querySelector('[data-quiz-final-button]');
      if (!button) continue;
      button.addEventListener('click', function (event) {
        var atc = buyBox.querySelector('.js-subscribtion__add-to-cart');
        if (atc && !atc.disabled) {
          event.preventDefault();
          atc.click();
          return;
        }
        // fallback: smooth-scroll to the buy box, same helper as pv-cta-button.
        // The element is a <button> (not a hash link) on purpose — see the
        // section file: custom.js binds a preventDefault-ing smooth-scroll
        // handler to every a[href^="#"], which swallowed the click.
        event.preventDefault();
        var target = this.getAttribute('data-quiz-final-anchor');
        if (target) {
          if (!(window.__froyaScrollTo && __froyaScrollTo(target))) {
            var dest = document.getElementById(target);
            if (dest) dest.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    }
  }

  /**
   * Order attribution + completion event, via window.FroyaQuiz
   * (snippets/quiz-analytics.liquid). Done HERE, on the results page, because
   * this is where the result is shown and the add-to-cart lives; the helper also
   * re-checks the cart after an add. quiz_completed fires once per completion,
   * not on every reload of this page. Its own try/catch: a throw must not reach
   * start()'s fallback, which would swap the visitor's bundle for the default.
   */
  function reportResult(bundle, answers, completedAt) {
    try {
      var quiz = window.FroyaQuiz;
      if (!quiz) return;
      var quizId = quiz.IDS.RITUAL;
      quiz.tagCart(quizId, bundle);
      if (quiz.once(quizId, 'quiz_completed', completedAt || 'unknown')) {
        quiz.track('quiz_completed', { quiz_id: quizId, result: bundle, answers: answers });
      }
    } catch (e) {
      /* analytics must never break the results page */
    }
  }

  /* ----------------------------------------------------------------- run -- */

  function init() {
    var root = document.querySelector('[data-quiz-results]');
    if (!root) return;

    var saved = readState(root.dataset.storageKey || DEFAULT_KEY) || {};
    var answers = saved.answers && typeof saved.answers === 'object' ? saved.answers : {};
    var identity = saved.identity && typeof saved.identity === 'object' ? saved.identity : {};

    // Greeting. A blank or whitespace-only name counts as no name, so the
    // comma-first variant can never render as ", your results are in".
    var name = (identity.first_name || '').trim();
    var nameSlot = root.querySelector('[data-quiz-results-name]');
    if (name && nameSlot) {
      nameSlot.textContent = name;
      reveal(root, 'data-quiz-results-greeting', 'named');
    } else {
      reveal(root, 'data-quiz-results-greeting', 'anon');
    }

    reveal(root, 'data-quiz-results-empathy', answers.feeling);
    reveal(root, 'data-quiz-results-age-band', answers.age);

    // The concerns block lives in the buy box, outside this section's root.
    var concerns = document.querySelectorAll('[data-quiz-results-concerns]');
    for (var i = 0; i < concerns.length; i++) {
      reveal(concerns[i], 'data-quiz-results-age', answers.age);
      revealBullets(concerns[i], answers);
    }

    // Routine line (Q6) under the buy-box price — rendered into every buy box
    // by lp-main-product via snippets/quiz-results-routine.liquid, so this is
    // document-wide rather than scoped to the header's root.
    reveal(document, 'data-quiz-results-routine', answers.routine);

    var bundle = BUNDLE_BY_CONCERN[answers.main_concern] || DEFAULT_BUNDLE;
    revealBundle(bundle);
    tagBundleForms(bundle);
    tagDrawerQuickAdds(bundle);
    bindFinalCta(bundle);
    reportResult(bundle, answers, saved.completedAt);

    window.FroyaRitualResults = {
      getAnswers: function () { return JSON.parse(JSON.stringify(answers)); },
      getIdentity: function () { return JSON.parse(JSON.stringify(identity)); },
      getBundle: function () { return bundle; }
    };

    document.dispatchEvent(new CustomEvent('quiz-results:ready', {
      detail: { answers: answers, identity: identity, bundle: bundle }
    }));
  }

  /**
   * A thrown error would leave every bundle hidden — the page would have no buy
   * box at all, because the reveal is what makes one visible. So a failure falls
   * back to the default bundle rather than an empty page. This is a real guard,
   * not a timing workaround: it runs once, immediately, on the error path only.
   */
  function start() {
    try {
      init();
    } catch (e) {
      try {
        revealBundle(DEFAULT_BUNDLE);
        bindFinalCta(DEFAULT_BUNDLE);
        // Must also tag here. The revealed buy box shows the discounted price
        // from its section settings regardless of whether this script got that
        // far, so skipping the tag would display $100 and charge $119.25.
        tagBundleForms(DEFAULT_BUNDLE);
        tagDrawerQuickAdds(DEFAULT_BUNDLE);
      } catch (e2) {
        /* nothing further we can do client-side; <noscript> covers no-JS */
      }
      if (window.console && console.warn) {
        console.warn('[quiz-results] personalisation failed, showing the default bundle', e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
